import {
  FilterX,
  Building2,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Percent,
  Store,
  Tag,
  Activity,
  RotateCcw,
  Sparkles,
  SlidersHorizontal,
  CheckCircle,
} from 'lucide-react';
import { useEffect, useMemo, useState, useCallback, memo } from 'react';

import { AutoCompleteVirual } from '@/components/custom-ui/VirtualSelect';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { Option } from '@/components/ui/multi-select';
import MultipleSelector from '@/components/ui/multi-select';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { formatNumberWithCommas } from '@/utils/FormatNumber';

import type { IItemCheckerReport } from '../../api/ItemChecker.interface';

// Numeric fields configuration with icons
const numericFieldsConfig: {
  field: keyof IItemCheckerReport;
  label: string;
  icon: React.ElementType;
  color: string;
}[] = [
  { field: 'Price', label: 'جمهور', icon: DollarSign, color: 'emerald' },
  { field: 'DiscountIN', label: 'مرجح', icon: Percent, color: 'purple' },
  { field: 'netQSales', label: 'بيع ق', icon: TrendingUp, color: 'green' },
  { field: 'netWSales', label: 'بيع ج', icon: TrendingUp, color: 'teal' },
  { field: 'Count_Store_1', label: 'رصيد ق', icon: Store, color: 'indigo' },
  { field: 'Count_Store_2', label: 'رصيد ج', icon: Store, color: 'violet' },
  { field: 'Count_Other', label: 'رصيد ب', icon: Store, color: 'blue' },
  { field: 'offersCount', label: 'العروض', icon: Tag, color: 'amber' },
  {
    field: 'offersMaxDiscount',
    label: 'أقصى خصم',
    icon: TrendingUp,
    color: 'green',
  },
  {
    field: 'offersMinDiscount',
    label: 'أقل خصم',
    icon: TrendingDown,
    color: 'red',
  },
];

// Status options with icons - moved outside component to prevent recreation
const retailStatusOptions: Option[] = [
  { label: 'فائض مخزون', value: 'overStock' },
  { label: 'يمكن التحويل', value: 'canTransfer' },
  { label: 'لا حركة', value: 'noTrans' },
  { label: 'يحتاج مخزون', value: 'needStock' },
];

const wholesaleStatusOptions: Option[] = [
  { label: 'فائض مخزون', value: 'overStock' },
  { label: 'يمكن التحويل', value: 'canTransfer' },
  { label: 'لا حركة', value: 'noTrans' },
  { label: 'يحتاج مخزون', value: 'needStock' },
];

type NumericFilters = Partial<
  Record<keyof IItemCheckerReport, { min: string; max: string }>
>;

type ItemFilterProps = {
  data: IItemCheckerReport[];
  onFilterChange: (filteredData: IItemCheckerReport[]) => void;
};

// Memoized numeric filter input component
const NumericFilterInput = memo(
  ({
    field,
    label,
    icon: Icon,
    color,
    dataMin,
    dataMax,
    value,
    onChange,
  }: {
    field: keyof IItemCheckerReport;
    label: string;
    icon: React.ElementType;
    color: string;
    dataMin: number;
    dataMax: number;
    value: { min: string; max: string };
    onChange: (
      field: keyof IItemCheckerReport,
      type: 'min' | 'max',
      value: string,
    ) => void;
  }) => {
    const colorClasses = useMemo(() => {
      const classes: Record<string, string> = {
        emerald:
          'from-emerald-50 to-emerald-100/50 border-emerald-200 dark:from-emerald-950/30 dark:to-emerald-900/20 dark:border-emerald-800',
        purple:
          'from-purple-50 to-purple-100/50 border-purple-200 dark:from-purple-950/30 dark:to-purple-900/20 dark:border-purple-800',
        green:
          'from-green-50 to-green-100/50 border-green-200 dark:from-green-950/30 dark:to-green-900/20 dark:border-green-800',
        teal: 'from-teal-50 to-teal-100/50 border-teal-200 dark:from-teal-950/30 dark:to-teal-900/20 dark:border-teal-800',
        indigo:
          'from-indigo-50 to-indigo-100/50 border-indigo-200 dark:from-indigo-950/30 dark:to-indigo-900/20 dark:border-indigo-800',
        violet:
          'from-violet-50 to-violet-100/50 border-violet-200 dark:from-violet-950/30 dark:to-violet-900/20 dark:border-violet-800',
        blue: 'from-blue-50 to-blue-100/50 border-blue-200 dark:from-blue-950/30 dark:to-blue-900/20 dark:border-blue-800',
        amber:
          'from-amber-50 to-amber-100/50 border-amber-200 dark:from-amber-950/30 dark:to-amber-900/20 dark:border-amber-800',
        red: 'from-red-50 to-red-100/50 border-red-200 dark:from-red-950/30 dark:to-red-900/20 dark:border-red-800',
      };
      return classes[color] || classes.blue;
    }, [color]);

    const iconColorClasses = useMemo(() => {
      const classes: Record<string, string> = {
        emerald: 'text-emerald-600 dark:text-emerald-400',
        purple: 'text-purple-600 dark:text-purple-400',
        green: 'text-green-600 dark:text-green-400',
        teal: 'text-teal-600 dark:text-teal-400',
        indigo: 'text-indigo-600 dark:text-indigo-400',
        violet: 'text-violet-600 dark:text-violet-400',
        blue: 'text-blue-600 dark:text-blue-400',
        amber: 'text-amber-600 dark:text-amber-400',
        red: 'text-red-600 dark:text-red-400',
      };
      return classes[color] || classes.blue;
    }, [color]);

    return (
      <div
        className={cn(
          'rounded-lg border p-3',
          'bg-gradient-to-r',
          colorClasses,
        )}
      >
        <div className="mb-2 flex items-center gap-2">
          <Icon className={cn('h-3.5 w-3.5', iconColorClasses)} />
          <label className="text-xs font-semibold">{label}</label>
          <Badge
            variant="outline"
            className="ml-auto h-4 border-border/50 px-1 text-[10px]"
          >
            {formatNumberWithCommas(dataMin)} -{' '}
            {formatNumberWithCommas(dataMax)}
          </Badge>
        </div>

        <div className="flex gap-2">
          <div className="relative flex-1">
            <Input
              className={cn(
                'h-8 pl-7 text-xs font-medium',
                'bg-background/80 backdrop-blur-sm',
                'border-border/50 dark:border-slate-700/50',
                'focus:border-primary/50 focus:ring-1 focus:ring-primary/20',
              )}
              type="number"
              placeholder="أقل"
              value={value.min}
              onChange={(e) => onChange(field, 'min', e.target.value)}
              min={dataMin}
              max={dataMax}
            />
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">
              من
            </span>
          </div>

          <div className="relative flex-1">
            <Input
              className={cn(
                'h-8 pl-7 text-xs font-medium',
                'bg-background/80 backdrop-blur-sm',
                'border-border/50 dark:border-slate-700/50',
                'focus:border-primary/50 focus:ring-1 focus:ring-primary/20',
              )}
              type="number"
              placeholder="أكبر"
              value={value.max}
              onChange={(e) => onChange(field, 'max', e.target.value)}
              min={dataMin}
              max={dataMax}
            />
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">
              إلى
            </span>
          </div>
        </div>
      </div>
    );
  },
);

NumericFilterInput.displayName = 'NumericFilterInput';

export function ItemFilter({ data, onFilterChange }: ItemFilterProps) {
  const [companies, setCompanies] = useState<{
    ID: number;
    Name: string;
  } | null>(null);

  const [places, setPlaces] = useState<{
    ID: number;
    Name: string;
  } | null>(null);

  const [numericFilters, setNumericFilters] = useState<NumericFilters>(() =>
    Object.fromEntries(
      numericFieldsConfig.map(({ field }) => [field, { min: '', max: '' }]),
    ),
  );

  const [retailStatus, setRetailStatus] = useState<Option[]>([]);
  const [wholesaleStatus, setWholesaleStatus] = useState<Option[]>([]);

  // Memoize company and place options
  const companyOptions = useMemo(() => {
    const uniqueCompanies = new Map<number, string>();
    data.forEach((item) => {
      if (!uniqueCompanies.has(item.Company_ID)) {
        uniqueCompanies.set(item.Company_ID, item.Company_Name);
      }
    });
    return Array.from(uniqueCompanies.entries()).map(([ID, Name]) => ({
      ID,
      Name,
    }));
  }, [data]);

  const placeOptions = useMemo(() => {
    const uniquePlaces = new Map<number, string>();
    data.forEach((item) => {
      if (item.Place_ID && !uniquePlaces.has(item.Place_ID)) {
        uniquePlaces.set(item.Place_ID, item.Place_Name || '');
      }
    });
    return Array.from(uniquePlaces.entries()).map(([ID, Name]) => ({
      ID,
      Name,
    }));
  }, [data]);

  // Memoize min/max calculations
  const minMaxValues = useMemo(() => {
    const result: Record<string, { min: number; max: number }> = {};

    numericFieldsConfig.forEach(({ field }) => {
      const values = data
        .map((item) => item[field] as number)
        .filter((v) => !isNaN(v));

      result[field] = values.length
        ? { min: Math.min(...values), max: Math.max(...values) }
        : { min: 0, max: 0 };
    });

    return result;
  }, [data]);

  // Optimized filter function with memoization
  const filteredData = useMemo(() => {
    let result = data;

    // Company filter
    if (companies) {
      result = result.filter((item) => item.Company_ID === companies.ID);
    }

    // Place filter
    if (places) {
      result = result.filter((item) => item.Place_ID === places.ID);
    }

    // Numeric filters - optimized loop
    const hasNumericFilters = Object.values(numericFilters).some(
      (filter) => filter.min !== '' || filter.max !== '',
    );

    if (hasNumericFilters) {
      result = result.filter((item) => {
        for (const { field } of numericFieldsConfig) {
          const filter = numericFilters[field];
          if (!filter) continue;

          const value = item[field] as number;
          if (filter.min !== '' && value < Number(filter.min)) return false;
          if (filter.max !== '' && value > Number(filter.max)) return false;
        }
        return true;
      });
    }

    // Status filters
    if (retailStatus.length > 0) {
      const statusValues = new Set(retailStatus.map((x) => x?.value));
      result = result.filter((item) => statusValues.has(item.retailStatus));
    }

    if (wholesaleStatus.length > 0) {
      const statusValues = new Set(wholesaleStatus.map((x) => x?.value));
      result = result.filter((item) => statusValues.has(item.wholesaleStatus));
    }

    return result;
  }, [companies, data, numericFilters, places, retailStatus, wholesaleStatus]);

  // Update filtered data with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      onFilterChange(filteredData);
    }, 150); // Small debounce to prevent excessive updates

    return () => clearTimeout(timer);
  }, [filteredData, onFilterChange]);

  // Memoized callbacks
  const handleNumericChange = useCallback(
    (field: keyof IItemCheckerReport, type: 'min' | 'max', value: string) => {
      setNumericFilters((prev) => ({
        ...prev,
        [field]: { ...prev[field], [type]: value },
      }));
    },
    [],
  );

  const resetFilters = useCallback(() => {
    setNumericFilters(
      Object.fromEntries(
        numericFieldsConfig.map(({ field }) => [field, { min: '', max: '' }]),
      ),
    );
    setCompanies(null);
    setPlaces(null);
    setRetailStatus([]);
    setWholesaleStatus([]);
  }, []);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (companies) count++;
    if (places) count++;
    if (retailStatus.length > 0) count++;
    if (wholesaleStatus.length > 0) count++;

    Object.values(numericFilters).forEach((filter) => {
      if (filter.min !== '' || filter.max !== '') count++;
    });

    return count;
  }, [companies, places, retailStatus, wholesaleStatus, numericFilters]);

  const hasActiveFilters = activeFilterCount > 0;

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'relative h-9 gap-2 overflow-hidden transition-all duration-300',
            'bg-gradient-to-r from-background to-muted/10',
            'hover:from-primary/5 hover:to-primary/10',
            'border-border/50 dark:border-slate-700/50',
            'group hover:scale-105 hover:shadow-md',
            hasActiveFilters &&
              'border-primary/50 from-primary/5 to-primary/10',
          )}
        >
          <div className="absolute inset-0 translate-x-[-100%] bg-gradient-to-r from-transparent via-primary/10 to-transparent transition-transform duration-700 group-hover:translate-x-[100%]" />
          <SlidersHorizontal
            className={cn(
              'h-4 w-4 transition-all duration-300',
              hasActiveFilters && 'text-primary',
              'group-hover:rotate-180',
            )}
          />
          <span className="font-medium">فلاتر</span>
          {activeFilterCount > 0 && (
            <Badge
              variant="secondary"
              className={cn(
                'h-5 px-1.5 text-[10px] font-bold',
                'bg-primary text-primary-foreground',
                'animate-in fade-in-50 zoom-in-95',
              )}
            >
              {activeFilterCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>

      <SheetContent
        side="left"
        className={cn(
          'w-[400px] overflow-hidden p-0',
          'bg-gradient-to-br from-background via-background to-muted/5',
          'dark:from-slate-900 dark:via-slate-900 dark:to-slate-800/30',
          'border-border/50 dark:border-slate-700/50',
          'data-[state=closed]:animate-none data-[state=open]:animate-none',
        )}
      >
        {/* Header */}
        <div
          className={cn(
            'sticky top-0 z-10',
            'bg-gradient-to-r from-red-50/50 via-orange-50/50 to-amber-50/50',
            'dark:from-red-950/20 dark:via-orange-950/20 dark:to-amber-950/20',
            'border-b border-border/30 backdrop-blur-md',
          )}
        >
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-gradient-to-br from-red-600 to-orange-600 p-2 text-white shadow-lg">
                <FilterX className="h-5 w-5" />
              </div>
              <div>
                <SheetTitle className="text-lg font-bold">
                  فلاتر البحث المتقدمة
                </SheetTitle>
                <SheetDescription className="text-xs">
                  تصفية {data.length} صنف
                </SheetDescription>
              </div>
            </div>

            <Button
              size="sm"
              disabled={!hasActiveFilters}
              variant="ghost"
              onClick={resetFilters}
              className={cn(
                'h-8 gap-1.5 transition-all duration-300',
                'hover:bg-destructive/10 hover:text-destructive',
                'dark:hover:bg-destructive/20',
                hasActiveFilters && 'animate-pulse',
              )}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              <span className="text-xs font-medium">مسح</span>
            </Button>
          </div>
        </div>

        <ScrollArea className="h-[calc(100vh-80px)]">
          <div className="space-y-4 p-4">
            {/* Status Filters Section */}
            <Card
              className={cn(
                'border-border/50 p-4',
                'bg-gradient-to-br from-background to-muted/5',
                'dark:from-slate-900/50 dark:to-slate-800/30',
              )}
            >
              <div className="mb-3 flex items-center gap-2">
                <Activity className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold">حالة المخزون</h3>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="mb-1.5 block text-xs text-muted-foreground">
                    حالة القطاعي
                  </label>
                  <MultipleSelector
                    placeholder="اختر حالة القطاعي..."
                    options={retailStatusOptions}
                    value={retailStatus}
                    onChange={setRetailStatus}
                    className={cn(
                      'bg-background/80',
                      'border-border/50 dark:border-slate-700/50',
                    )}
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-xs text-muted-foreground">
                    حالة الجملة
                  </label>
                  <MultipleSelector
                    placeholder="اختر حالة الجملة..."
                    options={wholesaleStatusOptions}
                    value={wholesaleStatus}
                    onChange={setWholesaleStatus}
                    className={cn(
                      'bg-background/80',
                      'border-border/50 dark:border-slate-700/50',
                    )}
                  />
                </div>
              </div>
            </Card>

            {/* Company and Place Filters */}
            <Card
              className={cn(
                'border-border/50 p-4',
                'bg-gradient-to-br from-background to-muted/5',
                'dark:from-slate-900/50 dark:to-slate-800/30',
              )}
            >
              <div className="mb-3 flex items-center gap-2">
                <Building2 className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold">التصنيفات</h3>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="mb-1.5 block text-xs text-muted-foreground">
                    الشركة
                  </label>
                  <AutoCompleteVirual
                    options={companyOptions}
                    value={companies}
                    searchPlaceholder="بحث عن شركة..."
                    onValueChange={setCompanies}
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-xs text-muted-foreground">
                    النوع
                  </label>
                  <AutoCompleteVirual
                    options={placeOptions}
                    value={places}
                    searchPlaceholder="بحث عن نوع..."
                    onValueChange={setPlaces}
                  />
                </div>
              </div>
            </Card>

            {/* Numeric Filters */}
            <Card
              className={cn(
                'border-border/50 p-4',
                'bg-gradient-to-br from-background to-muted/5',
                'dark:from-slate-900/50 dark:to-slate-800/30',
              )}
            >
              <div className="mb-3 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold">القيم العددية</h3>
              </div>

              <div className="space-y-3">
                {numericFieldsConfig.map(({ field, label, icon, color }) => (
                  <NumericFilterInput
                    key={field}
                    field={field}
                    label={label}
                    icon={icon}
                    color={color}
                    dataMin={minMaxValues[field]?.min || 0}
                    dataMax={minMaxValues[field]?.max || 0}
                    value={numericFilters[field] || { min: '', max: '' }}
                    onChange={handleNumericChange}
                  />
                ))}
              </div>
            </Card>

            {/* Active Filters Summary */}
            {hasActiveFilters && (
              <Card
                className={cn(
                  'border-primary/30 p-3',
                  'bg-gradient-to-r from-primary/5 to-primary/10',
                  'dark:from-primary/10 dark:to-primary/20',
                )}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    <span className="text-xs font-medium">
                      {activeFilterCount} فلتر نشط
                    </span>
                  </div>
                  <Badge variant="secondary" className="text-[10px]">
                    {filteredData.length} / {data.length} صنف
                  </Badge>
                </div>
              </Card>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
